import * as types from '../mutation-types';
import LifecycleStagesAPI from '../../api/lifecycleStages';

const normalizeStages = payload => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.payload)) {
    return payload.payload;
  }

  return [];
};

const normalizeStage = payload => {
  if (payload?.id) {
    return payload;
  }

  if (payload?.payload?.id) {
    return payload.payload;
  }

  return null;
};

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getAllLifecycleStages($state) {
    return Array.isArray($state.records) ? $state.records : [];
  },
  getLifecycleStages($state) {
    const records = Array.isArray($state.records) ? $state.records : [];
    return records.filter(s => s.stage_type === 'lifecycle');
  },
  getLostStages($state) {
    const records = Array.isArray($state.records) ? $state.records : [];
    return records.filter(s => s.stage_type === 'lost');
  },
  getLifecycleUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.default.SET_LIFECYCLE_UI_FLAG, { isFetching: true });
    try {
      const response = await LifecycleStagesAPI.get();
      commit(types.default.SET_LIFECYCLE_STAGES, normalizeStages(response.data));
    } catch (error) {
      // ignore
    } finally {
      commit(types.default.SET_LIFECYCLE_UI_FLAG, { isFetching: false });
    }
  },

  create: async ({ commit }, stageData) => {
    commit(types.default.SET_LIFECYCLE_UI_FLAG, { isCreating: true });
    try {
      const response = await LifecycleStagesAPI.create(stageData);
      const stage = normalizeStage(response.data);
      if (stage) {
        commit(types.default.ADD_LIFECYCLE_STAGE, stage);
      }
      return stage;
    } catch (error) {
      const message = error?.response?.data?.message || 'Error creating stage';
      throw new Error(message);
    } finally {
      commit(types.default.SET_LIFECYCLE_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit }, { id, ...stageData }) => {
    commit(types.default.SET_LIFECYCLE_UI_FLAG, { isUpdating: true });
    try {
      const response = await LifecycleStagesAPI.update(id, stageData);
      const stage = normalizeStage(response.data);
      if (stage) {
        commit(types.default.EDIT_LIFECYCLE_STAGE, stage);
      }
      return stage;
    } catch (error) {
      const message = error?.response?.data?.message || 'Error updating stage';
      throw new Error(message);
    } finally {
      commit(types.default.SET_LIFECYCLE_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit }, id) => {
    commit(types.default.SET_LIFECYCLE_UI_FLAG, { isDeleting: true });
    try {
      await LifecycleStagesAPI.delete(id);
      commit(types.default.DELETE_LIFECYCLE_STAGE, id);
    } catch (error) {
      const message = error?.response?.data?.message || 'Error deleting stage';
      throw new Error(message);
    } finally {
      commit(types.default.SET_LIFECYCLE_UI_FLAG, { isDeleting: false });
    }
  },

  reorder: async ({ commit }, stages) => {
    try {
      const response = await LifecycleStagesAPI.reorder(
        stages.map((s, idx) => ({ id: s.id, position: idx }))
      );
      commit(types.default.SET_LIFECYCLE_STAGES, normalizeStages(response.data));
    } catch (error) {
      // ignore
    }
  },
};

export const mutations = {
  [types.default.SET_LIFECYCLE_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.default.SET_LIFECYCLE_STAGES]($state, stages) {
    $state.records = normalizeStages(stages);
  },
  [types.default.ADD_LIFECYCLE_STAGE]($state, stage) {
    const normalizedStage = normalizeStage(stage);
    if (!normalizedStage) {
      return;
    }
    if (!Array.isArray($state.records)) {
      $state.records = [];
    }
    $state.records.push(normalizedStage);
  },
  [types.default.EDIT_LIFECYCLE_STAGE]($state, stage) {
    const normalizedStage = normalizeStage(stage);
    if (!normalizedStage) {
      return;
    }
    if (!Array.isArray($state.records)) {
      $state.records = [];
    }
    const idx = $state.records.findIndex(r => r.id === normalizedStage.id);
    if (idx !== -1) {
      $state.records.splice(idx, 1, {
        ...$state.records[idx],
        ...normalizedStage,
      });
    }
  },
  [types.default.DELETE_LIFECYCLE_STAGE]($state, id) {
    if (!Array.isArray($state.records)) {
      $state.records = [];
      return;
    }
    $state.records = $state.records.filter(r => r.id !== id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
